import { useMemo } from "react";

import {
  MutateOptions,
  UseMutateFunction,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  filter,
  find,
  findIndex,
  flatMap,
  isEmpty,
  map,
  partition,
} from "lodash/fp";
import { DateTime } from "luxon";

import {
  bulkPostRecommendationsAction,
  getRecommendationsForUser,
  postRecommendationAction,
} from "@app/analytics/api/recommendations";
import SAMPLE_RECOMMENDATIONS from "@app/analytics/Recommendations/sampleRecommendations";
import type { RecommendationCategoryType } from "@app/analytics/Recommendations/types";
import {
  Recommendation,
  RecommendationAction,
  GroupedRecommendation,
} from "@app/analytics/types";
import * as RecommendationUtils from "@app/analytics/utils/recommendationUtils";
import useAuth from "@app/common/auth/useAuth";
import { showToast } from "@app/common/hooks/useToasts";
import Config from "@app/config";
import * as Arrays from "@app/utils/arrays";

type MakeRecommendationActionFunction = UseMutateFunction<
  any,
  unknown,
  { action: RecommendationAction }
>;

type BulkMutateOptions = MutateOptions<
  any,
  unknown,
  { recommendations: { action: RecommendationAction }[]; isAccepted: boolean },
  unknown
>;
type BulkRecommendationsMutateFunction = (
  recommendation: GroupedRecommendation,
  opts?: BulkMutateOptions
) => void;

export interface UseRecommendations {
  completedRecommendations: Recommendation[];
  error: boolean;
  loading: boolean;
  allUserRecommendations: Recommendation[];
  pendingRecommendations: Recommendation[];
  acceptRecommendation: MakeRecommendationActionFunction;
  rejectRecommendation: MakeRecommendationActionFunction;
  acceptAllRecommendations: BulkRecommendationsMutateFunction;
  rejectAllRecommendations: BulkRecommendationsMutateFunction;
  isRefetching: boolean;
  actionIsLoading: boolean;
  refetch: UseQueryResult["refetch"];
  isSampleData: boolean;
  groupedPendingRecommendations: GroupedRecommendation[];
  reviewedRecommendationsByCategory: {
    [key in RecommendationCategoryType]: Recommendation[];
  };
}

interface UseRecommendationParams {
  enabled?: boolean;
  useSample?: boolean;
}

const DEFAULT_PARAMS: UseRecommendationParams = {
  enabled: true,
  useSample: false,
};

const EMPTY_RECOMMENDATIONS: Recommendation[] = [];

const getQueryKey = (userId: string) => ["recommendations", userId];

const useRecommendations = (
  opts: UseRecommendationParams = DEFAULT_PARAMS
): UseRecommendations => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = getQueryKey(user?.id ?? "no_user");

  const recommendationsQuery = useQuery({
    queryKey,
    queryFn: () => getRecommendationsForUser(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 30 * 60 * 1000,
    enabled:
      !Config.api.disableMetricsQueries && opts.enabled && Boolean(user?.id),
  });

  const updateRecommendationMutation = useMutation({
    mutationFn: postRecommendationAction,
    onMutate: async ({ action, isAccepted }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousRecommendations: Recommendation[] =
        queryClient.getQueryData(queryKey) ?? [];
      const previousRecommendationIndex = findIndex(
        (rec) =>
          Boolean(find({ operationToken: action.operationToken }, rec.actions)),
        previousRecommendations
      );

      // Don't do any optimistic updating if we can't
      // find the recommendation in the query cache
      if (previousRecommendationIndex === -1) {
        return;
      }

      const previousRecommendation =
        previousRecommendations[previousRecommendationIndex];
      const newRecommendation: Recommendation = {
        ...previousRecommendation,
        completedOn: DateTime.now(),
        status: isAccepted ? "accepted" : "rejected",
      };

      queryClient.setQueryData(
        queryKey,
        Arrays.replaceAt(
          previousRecommendations,
          newRecommendation,
          previousRecommendationIndex
        )
      );
      return { previousRecommendations };
    },
    onError: (_err, _newRecommendations, context) => {
      if (context) {
        queryClient.setQueryData(queryKey, context.previousRecommendations);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey,
      }),
  });

  const bulkUpdateRecommendationsMutation = useMutation({
    mutationFn: bulkPostRecommendationsAction,
    onSuccess: (_data, { isAccepted }) => {
      showToast({
        severity: "info",
        message: `All recommendations ${isAccepted ? "accepted" : "ignored"}`,
      });
      recommendationsQuery.refetch();
    },
  });

  const executeRecommendationAction =
    (isAccepted: boolean): MakeRecommendationActionFunction =>
    ({ action }, ...rest) =>
      updateRecommendationMutation.mutate({ action, isAccepted }, ...rest);

  const acceptRecommendation = executeRecommendationAction(true);
  const rejectRecommendation = executeRecommendationAction(false);

  const bulkExecuteRecommendationActions =
    (isAccepted: boolean) =>
    (recommendation: GroupedRecommendation, opts?: BulkMutateOptions) => {
      const recs = map(
        (rec) => ({ action: RecommendationUtils.getDefaultAction(rec) }),
        recommendation.recommendations
      );
      bulkUpdateRecommendationsMutation.mutate(
        {
          recommendations: recs,
          isAccepted,
        },
        opts
      );
    };

  const acceptAllRecommendations = bulkExecuteRecommendationActions(true);
  const rejectAllRecommendations = bulkExecuteRecommendationActions(false);

  let isSampleData = false;
  let recommendations = recommendationsQuery.data ?? EMPTY_RECOMMENDATIONS;

  if (
    opts.useSample &&
    isEmpty(recommendations) &&
    !recommendationsQuery.isInitialLoading
  ) {
    recommendations = SAMPLE_RECOMMENDATIONS;
    isSampleData = true;
  }

  const allUserRecommendations = useMemo(
    () =>
      filter(
        "meetingContext",
        flatMap(
          (
            recommendation: Recommendation
          ): Recommendation | Recommendation[] => {
            return isEmpty(recommendation.subRecommendations)
              ? recommendation
              : [...recommendation.subRecommendations];
          },
          recommendations
        )
      ),
    [recommendations]
  );

  const [pendingRecommendations, completedRecommendations] = useMemo(
    () => partition((rec) => rec.status === "pending", allUserRecommendations),
    [allUserRecommendations]
  );

  return {
    error: recommendationsQuery.isError,
    loading: recommendationsQuery.isInitialLoading,
    isRefetching: recommendationsQuery.isRefetching,
    allUserRecommendations: allUserRecommendations,
    completedRecommendations,
    pendingRecommendations,
    rejectRecommendation,
    acceptRecommendation,
    acceptAllRecommendations,
    rejectAllRecommendations,
    actionIsLoading: updateRecommendationMutation.isPending,
    refetch: recommendationsQuery.refetch,
    isSampleData,
    groupedPendingRecommendations: RecommendationUtils.groupRecommendations(
      pendingRecommendations
    ),
    reviewedRecommendationsByCategory:
      RecommendationUtils.groupReviewedRecommendations(
        completedRecommendations
      ),
  };
};

export default useRecommendations;
